import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './pages/Login';
import CustomerDashboard from './pages/CustomerDashboard';
import AdminLayout from './components/AdminLayout';
import LicensesPage from './pages/admin/LicensesPage';
import CustomersPage from './pages/admin/CustomersPage';
import EventsPage from './pages/admin/EventsPage';
import SettingsPage from './pages/admin/SettingsPage';

interface PrivateRouteProps {
  children: JSX.Element;
  requiredRole?: string;
}

function PrivateRoute({ children, requiredRole }: PrivateRouteProps) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requiredRole && user.role !== requiredRole) {
    return <Navigate to="/" />;
  }

  return children;
}

function AppRouter() {
  const { user } = useAuth();

  return (
    <Routes>
      <Route path="/login" element={<Login />} />

      {/* Admin Routes with Layout */}
      <Route
        path="/admin"
        element={
          <PrivateRoute requiredRole="admin">
            <AdminLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/admin/licenses" replace />} />
        <Route path="licenses" element={<LicensesPage />} />
        <Route path="customers" element={<CustomersPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Customer Routes */}
      <Route
        path="/customer/*"
        element={
          <PrivateRoute requiredRole="customer">
            <CustomerDashboard />
          </PrivateRoute>
        }
      />

      {/* Default redirect based on role */}
      <Route
        path="/"
        element={
          user ? (
            user.role === 'admin' ? <Navigate to="/admin/licenses" /> : <Navigate to="/customer" />
          ) : (
            <Navigate to="/login" />
          )
        }
      />

      {/* Catch-all redirect */}
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
